import type { Ledger } from "./SiteHeader.inert";
import { createDisclosure } from "./SiteHeader.disclosure";
import { focusFirstStop } from "./SiteHeader.focus";
import { giveBack, write } from "./SiteHeader.inert";
import { createPanel } from "./SiteHeader.panel";
import { EPanel, getHeaderStore, takePanel } from "./SiteHeader.store";

const BRANDS = "[data-fa-header-brands]";
const DRAWER = "[data-fa-header-brands-drawer]";
const SCRIM = "[data-fa-header-brands-scrim]";
const TOGGLE = "[data-fa-brand-switcher-toggle]";
const CLOSE_WORD = "[data-fa-brand-switcher-close]";
const SWITCHER = "[data-fa-brand-switcher]";
const SWITCHER_OPEN = "fa-brand-switcher--open";

/** Returns its restore, which the header's teardown owns — listeners ride the caller's signal. */
export const initBrand = (root: HTMLElement, signal: AbortSignal): (() => void) => {
	const brands = root.querySelector<HTMLElement>(BRANDS);
	const toggle = root.querySelector<HTMLElement>(TOGGLE);
	const drawer = brands?.querySelector<HTMLElement>(DRAWER);
	const scrim = root.querySelector<HTMLElement>(SCRIM);

	/* The scrim is optional: it dims and offers a click-out, and the pull opens without either. */
	if (!brands || !toggle || !drawer) return () => undefined;

	const state: Ledger = new Map();
	const store = getHeaderStore(root);
	const switcher = root.querySelector<HTMLElement>(SWITCHER);

	/* Driven by the field, not by `aria-expanded`: the attribute flips on the press so the button
	   answers at once, while the lockup must not morph until the outgoing panel has left. */
	const paintSwitcher = () => {
		switcher?.classList.toggle(SWITCHER_OPEN, store.read().panel === EPanel.BRANDS);
	};

	const unpaint = store.subscribe(paintSwitcher);

	const isOpen = () => toggle.getAttribute("aria-expanded") === "true";

	const panel = createPanel({
		key: EPanel.BRANDS,
		root,
		settles: brands,
		returnTo: () => toggle,
		isOpen,
		release: () => {
			giveBack(state);
			if (drawer.contains(document.activeElement)) toggle.focus({ preventScroll: true });
			drawer.inert = true;
		},
	});

	const close = panel.close;

	const open = () => {
		/* Held before the field changes, so the page stays held across a hand-over: the lock counts
		   holds, and releasing the outgoing panel's would otherwise unlock mid-swap. */
		panel.hold();

		void takePanel(store, EPanel.BRANDS, {
			claim: () => {
				write(state, toggle, "aria-expanded", "true");
				drawer.inert = false;
				focusFirstStop(drawer);
			},
			stillWanted: isOpen,
		});
	};

	const options = { signal };

	toggle.addEventListener(
		"click",
		() => {
			/* `false`: the click already focused the toggle — see SiteHeader.drawer.ts. */
			if (isOpen()) close(false);
			else open();
		},
		options,
	);

	scrim?.addEventListener(
		"click",
		() => {
			close(true);
		},
		options,
	);

	switcher?.querySelector<HTMLElement>(CLOSE_WORD)?.addEventListener(
		"click",
		() => {
			close(true);
		},
		options,
	);

	createDisclosure({ scope: brands, contains: drawer, isOpen, close, signal });

	return () => {
		panel.dispose();
		unpaint();
		switcher?.classList.remove(SWITCHER_OPEN);
	};
};
